import { getCurrentUserProfile, updateProfile as updateProfileApi } from '@repo/api';
import { createSlice } from '../factory';
import { runWithDedup } from '../utils';
export const profileSlice = createSlice({
    name: 'profile',
    initialState: {
        profile: null,
        emailAuthToken: null,
        isLoading: false,
        lastSynced: null,
        isBlurRequired: false,
    },
    // Persist the last known profile so UI can render immediately on reload
    persist: { keys: ['profile', 'emailAuthToken'] },
    actions: ({ get, setSlice }) => {
        /**
         * Helper function to set profile data with consistent state updates
         */
        const updateProfileState = (profile) => {
            const extractedToken = profile
                ? (profile.emailAuthToken ?? profile.email_auth_token ?? null)
                : null;
            setSlice({
                profile,
                emailAuthToken: extractedToken ?? get()?.profile?.emailAuthToken ?? null,
                isLoading: false,
                lastSynced: profile ? Date.now() : null,
            });
        };
        return {
            /**
             * Sync isBlurRequired based on explorer and reviewer data
             * Called automatically when explorer or reviewer data changes
             */
            syncIsBlurRequired: () => {
                const state = get();
                const explorerData = state?.explorer?.data;
                const reviewer = state?.reviewer?.data;
                const isBlurRequired = !(!!explorerData?.accessType || !!reviewer?.hasReviewerAccess);
                setSlice({ isBlurRequired });
            },
            /**
             * Set profile data
             * Used when profile is received from verifyOtp or other sources
             */
            setProfile: (profile) => {
                updateProfileState(profile);
            },
            /**
             * Set email auth token (used to append `email_auth_token` to v4 links).
             * This is separate from `profile` because the backend profile may not include it.
             */
            setEmailAuthToken: (token) => {
                const t = (token || '').trim();
                setSlice(prev => ({
                    ...prev,
                    emailAuthToken: t || null,
                }));
            },
            /**
             * Fetch current user profile from API
             * Syncs profile state with backend
             */
            syncProfile: async () => {
                const key = 'profile.syncProfile';
                setSlice(prev => ({ ...prev, isLoading: true }));
                try {
                    const exec = async () => runWithDedup(key, () => getCurrentUserProfile());
                    const loading = get()?.loading;
                    const profile = loading?.withLoading
                        ? await loading.withLoading(key, exec)
                        : await exec();
                    updateProfileState(profile);
                    return profile;
                }
                catch (error) {
                    setSlice(prev => ({ ...prev, isLoading: false }));
                    throw error;
                }
            },
            /**
             * Update profile
             * Updates profile data and syncs with backend
             */
            updateProfile: async (data) => {
                const currentProfile = get()?.profile?.profile ?? get().profile;
                // Optimistic update
                if (currentProfile) {
                    setSlice(prev => ({
                        ...prev,
                        profile: { ...currentProfile, ...data },
                    }));
                }
                try {
                    const key = 'profile.updateProfile';
                    const exec = async () => runWithDedup(key, () => updateProfileApi(data));
                    const loading = get()?.loading;
                    const updatedProfile = loading?.withLoading
                        ? await loading.withLoading(key, exec)
                        : await exec();
                    updateProfileState(updatedProfile);
                    return updatedProfile;
                }
                catch (error) {
                    // Rollback on error
                    if (currentProfile) {
                        setSlice(prev => ({ ...prev, profile: currentProfile }));
                    }
                    throw error;
                }
            },
            /**
             * Clear profile data
             * Used on logout
             */
            clearProfile: () => {
                setSlice({
                    profile: null,
                    emailAuthToken: null,
                    isLoading: false,
                    lastSynced: null,
                    isBlurRequired: true,
                });
            },
        };
    },
});
